import MarkdownIt from 'markdown-it'
import taskLists from 'markdown-it-task-lists'
import footnote from 'markdown-it-footnote'
import type { FileItem } from '../types'

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true,
})

md.use(taskLists, { disabled: true })
md.use(footnote)

export function renderMarkdown(content: string): string {
  return md.render(content)
}

export interface TocItem {
  level: number
  text: string
  id: string
}

export function extractToc(content: string): TocItem[] {
  const toc: TocItem[] = []
  const headingRegex = /^(#{1,6})\s+(.+)$/gm
  let match

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length
    const text = match[2].trim()
    const id = text.toLowerCase().replace(/[^\w\u4e00-\u9fff]+/g, '-')
    toc.push({ level, text, id })
  }

  return toc
}

export function generateId(text: string): string {
  return text.toLowerCase().replace(/[^\w\u4e00-\u9fff]+/g, '-')
}

export function formatMarkdown(content: string, type: string, wrap?: string): string {
  switch (type) {
    case 'bold':
      return wrap ? `**${wrap}**` : `**${content}**`
    case 'italic':
      return wrap ? `*${wrap}*` : `*${content}*`
    case 'strikethrough':
      return wrap ? `~~${wrap}~~` : `~~${content}~~`
    case 'code':
      return wrap ? `\`${wrap}\`` : `\`${content}\``
    case 'codeblock':
      return wrap ? `\n\`\`\`\n${wrap}\n\`\`\`\n` : `\n\`\`\`\n${content}\n\`\`\`\n`
    case 'heading1':
      return `# ${content.replace(/^#+\s*/, '')}`
    case 'heading2':
      return `## ${content.replace(/^#+\s*/, '')}`
    case 'heading3':
      return `### ${content.replace(/^#+\s*/, '')}`
    case 'link':
      return `[${wrap || 'text'}](url)`
    case 'image':
      return `![${wrap || 'alt'}](url)`
    case 'quote':
      return content
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n')
    case 'unorderedList':
      return content
        .split('\n')
        .map((line) => `- ${line}`)
        .join('\n')
    case 'orderedList':
      return content
        .split('\n')
        .map((line, i) => `${i + 1}. ${line}`)
        .join('\n')
    case 'taskList':
      return content
        .split('\n')
        .map((line) => `- [ ] ${line}`)
        .join('\n')
    case 'table':
      return `\n| Header 1 | Header 2 | Header 3 |\n|----------|----------|----------|\n| Cell 1   | Cell 2   | Cell 3   |\n| Cell 4   | Cell 5   | Cell 6   |\n`
    case 'horizontalRule':
      return '\n---\n'
    default:
      return content
  }
}

export function createNewFile(name: string): FileItem {
  return {
    id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name,
    content: '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    isFolder: false,
    parentId: null,
  }
}
